import React, { useEffect, useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { api } from "@services/api";
import { useMutation } from "@tanstack/react-query";
import { runInquiry } from "@telemetry/runInquiry";
import { checkPermission } from "@utils/user-permissions";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { handleSingleVesselRedirect, hasAlreadyActiveVesselCall } from "./utils";
import { vesselScanSchema } from "./VesselScanPageSchema";
import type { VesselScanResponse } from "./VesselScanResults.types";

const LANG_BASE = "vessel-call";
const SCAN_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Vessel Scan",
  action: "scan_vessels",
  entityType: "vessel",
};

const VesselScanPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { notify } = useNotification();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { setField } = useVesselCallFormStore();
  const { userActionPermissions } = useAuthStore();
  const methods = useForm<{ code: string }>({ defaultValues: { code: "" } });
  const { setError, clearErrors } = methods;
  const [code, setCode] = useState("");
  const PAGE_NUMBER = 0;
  const PAGE_SIZE = 25;
  const trimmedCode = code.trim();
  const isImoSearch = /^[0-9]+$/.test(trimmedCode);

  useEffect(() => {
    if (!checkPermission(userActionPermissions, "canWriteVesselCall")) {
      navigate("/vessel-call");
    }
  }, []);

  const mutation = useMutation({
    mutationFn: (value: string) =>
      runInquiry(SCAN_INQUIRY_CONTEXT, () =>
        api.get<VesselScanResponse>("bff-vessel", "vessels:scan", {
          params: {
            imoOrName: value,
            pageNumber: PAGE_NUMBER,
            pageSize: PAGE_SIZE,
          },
        }),
      ),
    onSuccess: async (payload) => {
      const vessels = payload?.vessels || [];
      if (vessels.length === 1) {
        await handleSingleVesselRedirect(vessels[0], navigate);
        notify({
          title: t(`${LANG_BASE}.vessel-scan.success`),
          type: "success",
        });
        setField("vesselId", vessels[0]?.id);
        return;
      }
      if (vessels.length > 1) {
        navigate("/vessel-call/scan-results", {
          state: {
            scanValue: code.trim(),
            vessels,
            pagination: {
              total: payload.totalRecordCount,
              pages: payload.totalPages,
              pageSize: payload.pageSize,
              pageNumber: payload.pageNumber,
            },
          },
        });
        return;
      }
      if (payload && vessels.length === 0) {
        notify({
          title: t(`${LANG_BASE}.vessel-scan.no-result`),
          description: isImoSearch
            ? t(`${LANG_BASE}.vessel-scan.not-registered-imo-description`, {
                imoNumber: trimmedCode,
              })
            : t(`${LANG_BASE}.vessel-scan.not-registered-name-description`, {
                vesselName: trimmedCode,
              }),
          type: "error",
        });
        return;
      }
      notify({
        title: t(`${LANG_BASE}.vessel-scan.scan-error-title`),
        description: t(`${LANG_BASE}.vessel-scan.scan-error-description`),
        type: "error",
      });
    },
    onError: (error: any) => {
      console.error("Vessel scan error:", error);
      if (hasAlreadyActiveVesselCall(error)) {
        notify({
          title: t(`${LANG_BASE}.vessel-scan.already-active-error-title`),
          description: t(`${LANG_BASE}.vessel-scan.already-active-error-description`),
          type: "error",
        });
        return;
      }
      notify({
        title: t(`${LANG_BASE}.vessel-scan.scan-error-title`),
        description:
          error.response?.data?.detail || t(`${LANG_BASE}.vessel-scan.scan-error-description`),
        type: "error",
      });
    },
  });

  const handleScan = () => {
    if (mutation.isPending) {
      return;
    }

    const label = t("vessel-call.vessel-scan.imo-or-vessel");

    const parsed = vesselScanSchema(t, label, isImoSearch).safeParse(trimmedCode);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError("code", {
        type: "manual",
        message: issue?.message || "form-validation.invalid",
      });
      return;
    }

    clearErrors("code");
    mutation.mutate(trimmedCode);
  };

  const handleFieldChange = (value: string) => {
    setCode(value);

    const label = t("vessel-call.vessel-scan.imo-or-vessel");
    const isDigitsOnly = /^[0-9]+$/.test(value.trim());
    const parsed = vesselScanSchema(t, label, isDigitsOnly).safeParse(value.trim());

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError("code", {
        type: "manual",
        message: issue?.message || "form-validation.invalid",
      });
    } else {
      clearErrors("code");
    }
  };

  return (
    <PageLayout dir={dir}>
      <Subheader
        title={t("vessel-call.create-vessel-call")}
        subtitle={
          <span>
            <span
              onClick={() => navigate("/vessel-call")}
              className="cursor-pointer"
              style={{ color: theme.palette.text.brand }}
            >
              {t("navigation.vessel-call")}
            </span>{" "}
            / {t("vessel-call.create-vessel-call")}
          </span>
        }
      />

      <FormProvider {...methods}>
        <div className="flex justify-center mt-12">
          <div
            className="w-[640px] rounded-lg overflow-hidden"
            style={{
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
            data-testid="vessel-scan-card"
          >
            <div className="py-3 px-5" style={{ background: theme.palette.grey[100] }}>
              <span
                className="text-sm font-extrabold"
                style={{ color: theme.palette.text.primary }}
              >
                {t("vessel-call.vessel-scan.title")}
              </span>
            </div>
            <div className="px-5 pt-6 pb-5">
              <FormItem
                name="code"
                type="text"
                label={t("vessel-call.vessel-scan.imo-or-vessel")}
                testId="vessel-scan-input"
                value={code}
                onFieldChange={(_, value) => handleFieldChange(value as string)}
              />
              <div className="flex justify-end gap-4 mt-5">
                <Button
                  variant="text"
                  color="secondary"
                  label={t("common.cancel")}
                  onClick={() => navigate("/vessel-call")}
                  testId="vessel-scan-cancel-button"
                  sx={{ textTransform: "none" }}
                />
                <Button
                  variant="contained"
                  onClick={handleScan}
                  testId="vessel-scan-scan-button"
                  sx={{
                    textTransform: "none",
                    minWidth: 108,
                    display: "flex",
                    justifyContent: "center",
                  }}
                  startIcon={
                    !mutation.isPending ? <ContentPasteGoIcon fontSize="small" /> : undefined
                  }
                  label={
                    mutation.isPending ? (
                      <RotateRightIcon
                        fontSize="small"
                        sx={{
                          "animation": "mui-spin 0.9s linear infinite",
                          "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
                        }}
                      />
                    ) : (
                      t("vessel-call.vessel-scan.scan")
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </PageLayout>
  );
};

export default VesselScanPage;
